import http from "../../utils/http";
import { calculatePercentage } from "../../utils/question";
import { permissions } from "../../utils/roles";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  resetQuestionState,
  setQuestionLoader,
  setQuestionnaireDetails,
  setQuestionSubmitting,
  toggleTranslation,
} from "../reducers/questionTranslationSlice";
import { AppThunk } from "../store";

export const loadQuestionDetails =
  (
    studyId: string | undefined,
    formId: string,
    lang: string,
    type: "platform" | "study",
    previewMode?: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!formId || !lang || (type === "study" && !studyId)) {
        dispatch(resetQuestionState());
        return;
      }

      dispatch(setQuestionLoader(true));
      const prefix =
        type === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      const { data } = await http.get(
        `${prefix}form-translations/${formId}?lang=${lang}`
      );

      if (data && data?.data?.form) {
        const editPermission = previewMode
          ? false
          : type === "platform"
          ? true
          : getState().study.studyPermissions.includes(
              permissions.studyDesigner
            ) && getState().study.status === "not_live";

        // Sort Questions
        let questions = data.data.form.questions
          ?.slice()
          .sort(
            (a: { position: number }, b: { position: number }) =>
              a.position - b.position
          );

        questions = questions.map((q: any) => {
          if (q.choices) {
            q.choices = q.choices.sort(
              (a: { position: number }, b: { position: number }) =>
                a.position - b.position
            );
          }
          if (q?.validations) {
            q.validations = q.validations.sort(
              (a: { position: number }, b: { position: number }) =>
                a.position - b.position
            );
          }
          q.labelMarkdown = q.label.replace(/(?:\r\n|\r|\n)/g, "<br>");
          return {
            ...q,
          };
        });

        dispatch(
          setQuestionnaireDetails({
            questionName: data.data.form.name,
            error: "",
            loading: false,
            editable: editPermission,
            formSubmitting: false,
            formLevel: type,
            translationToggle: false,

            status: "active",
            formDirty: false,

            showPropModal: false,
            modalQuestion: null,

            isBasicError: "",
            isChoicesError: "",
            isRepeatedConfigError: "",
            isGridConfigError: "",
            isValidationsError: "",
            isSummaryError: "",

            questions: questions,
            percentage: calculatePercentage(data),
            formId: formId,
            studyId: studyId || "",
            lang: lang,
          })
        );
      }
    } catch (err) {
      dispatch(setQuestionSubmitting(false));
      dispatch(setQuestionLoader(false));
      errorToastMessage(err as Error);
    }
  };

export const saveQuestion = (): AppThunk => async (dispatch, getState) => {
  const { formId, studyId, lang, modalQuestion, formLevel } =
    getState().questionTranslation;

  const body = {
    ...modalQuestion,
    choices: modalQuestion?.choices?.map((choice: any) => ({
      label: choice.label,
      imageUrl: choice.imageUrl,
      questionChoiceId: choice.questionChoiceId,
    })),
    summaryMap: modalQuestion?.summaryMap?.map((item: any) => {
      return {
        key: item.key,
        value: item.value,
      };
    }),
  };

  try {
    dispatch(setQuestionLoader(true));
    dispatch(setQuestionSubmitting(true));

    const prefix =
      formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

    const { data } = await http.post(
      `${prefix}form-translations/${formId}/question/${modalQuestion.questionId}?lang=${lang}`,
      body
    );

    toastMessage("success", data.message);
    dispatch(toggleTranslation());
  } catch (err) {
    dispatch(setQuestionSubmitting(false));
    dispatch(setQuestionLoader(false));
    errorToastMessage(err as Error);
  }
};

export const importTranslations =
  (file: File, lang: string): AppThunk =>
  async (dispatch, getState) => {
    const { formId, studyId, formLevel } = getState().questionTranslation;
    try {
      dispatch(setQuestionLoader(true));
      dispatch(setQuestionSubmitting(true));
      const formData = new FormData();
      formData.append("file", file);
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      let res = await http.post(
        `${prefix}form-translations/${formId}/import?lang=${lang}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toastMessage("success", res?.data?.message);
      dispatch(toggleTranslation());
    } catch (err) {
      dispatch(setQuestionSubmitting(false));
      dispatch(setQuestionLoader(false));
      errorToastMessage(err as Error);
    }
  };
